import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import './CloneWorkspaceModal.css';

function CloneWorkspaceModal({ isOpen, onClose, selectedWorkspace, onClone }) {
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [isCloning, setIsCloning] = useState(false);
  const [cloningResult, setCloningResult] = useState(null);

  useEffect(() => {
    if (isOpen && selectedWorkspace) {
      const name = selectedWorkspace.workspace_name || '';
      // Strip existing "(Copy)" or "(Copy N)" suffix to get base name
      const match = name.match(/^(.+?)\s*\(Copy(?:\s+(\d+))?\)\s*$/);
      if (match) {
        const baseName = match[1];
        const prevNum = match[2] ? parseInt(match[2], 10) : 1;
        setNewWorkspaceName(`${baseName} (Copy ${prevNum + 1})`);
      } else {
        setNewWorkspaceName(`${name} (Copy)`);
      }
      setIsCloning(false);
      setCloningResult(null);
    }
  }, [isOpen, selectedWorkspace]);

  const handleClone = async () => {
    if (!selectedWorkspace || !newWorkspaceName.trim()) return;

    setIsCloning(true);
    setCloningResult(null);

    try {
      const result = await onClone(selectedWorkspace, newWorkspaceName);
      const successResult = { success: true, ...result };
      setCloningResult(successResult);

      setTimeout(() => {
        onClose(successResult);
      }, 1500);
    } catch (error) {
      console.error('Clone error:', error);
      setCloningResult({
        success: false,
        error: error.message || 'Clone failed. Please try again.'
      });
    } finally {
      setIsCloning(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="clone-modal-overlay">
      <div className="clone-modal">
        <div className="clone-modal-header">
          <div className="clone-modal-title">
            <Copy size={20} />
            <span>Clone Workspace</span>
          </div>
          <button
            className="clone-modal-close"
            onClick={onClose}
            disabled={isCloning}
          >
            <X size={18} />
          </button>
        </div>

        <div className="clone-modal-content">
          {!cloningResult ? (
            <>
              <div className="clone-section">
                <p className="clone-description">
                  Create a copy of <strong>{selectedWorkspace?.workspace_name}</strong> with all its content. You'll be the owner of the new workspace.
                </p>
              </div>

              <div className="clone-section">
                <label className="clone-label" htmlFor="new-workspace-name">
                  New workspace name
                </label>
                <input
                  id="new-workspace-name"
                  type="text"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  placeholder="Enter a name for the cloned workspace"
                  className="clone-input"
                  disabled={isCloning}
                  autoFocus
                />
              </div>

              {isCloning && (
                <div className="clone-progress">
                  <div className="progress-bar">
                    <div className="progress-fill-indeterminate" />
                  </div>
                  <span className="clone-progress-label">Cloning workspace...</span>
                </div>
              )}
            </>
          ) : (
            <div className="clone-result">
              {cloningResult.success ? (
                <div className="result-success">
                  <CheckCircle size={40} className="result-icon success" />
                  <h3>Cloned</h3>
                  <p>Workspace has been cloned successfully.</p>
                </div>
              ) : (
                <div className="result-error">
                  <AlertTriangle size={40} className="result-icon error" />
                  <h3>Clone Failed</h3>
                  <p>{cloningResult.error}</p>
                  <button
                    className="retry-button"
                    onClick={() => setCloningResult(null)}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="clone-modal-footer">
          {!cloningResult ? (
            <>
              <button
                className="clone-cancel-button"
                onClick={onClose}
                disabled={isCloning}
              >
                Cancel
              </button>
              <button
                className="clone-confirm-button"
                onClick={handleClone}
                disabled={isCloning || !newWorkspaceName.trim()}
              >
                {isCloning ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Cloning...
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Clone
                  </>
                )}
              </button>
            </>
          ) : cloningResult.success ? (
            <button className="clone-close-button" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default CloneWorkspaceModal;
